import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { ApiResponse } from '@/api/handlers/responses';
import {
    invalidateDraftInvoiceCaches,
    removeDraftInvoiceRow,
    restoreDraftInvoiceLists,
    type DraftListSnapshot,
} from './draftInvoiceCache';
import { PurchaseQueryKeys } from './purchaseQueryKeys';

/**
 * Discards a draft (`DELETE /purchases/draft-invoices/{id}` — soft delete,
 * 204). The row is removed from the drafts-list caches **optimistically** in
 * `onMutate` (the table reacts before the round-trip); the envelope never
 * rejects, so a `success: false` settle restores the snapshot instead of
 * going through `onError`. On success the draft's detail cache entry is
 * **removed** (a later visit to the deleted draft's URL refetches and
 * surfaces the 404 instead of serving the stale draft) and the list + summary
 * are invalidated for reconciliation.
 */
export const useDeleteDraftInvoiceMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<void>,
        Error,
        string,
        { snapshot: DraftListSnapshot }
    >({
        mutationFn: (id) => apiClient.purchases.deleteDraftInvoice(id),
        onMutate: async (id) => {
            await queryClient.cancelQueries({
                queryKey: PurchaseQueryKeys.draftInvoiceLists(),
            });
            return { snapshot: removeDraftInvoiceRow(queryClient, id) };
        },
        onSuccess: (result, id, context) => {
            if (!result.success) {
                if (context) {
                    restoreDraftInvoiceLists(queryClient, context.snapshot);
                }
                return;
            }
            queryClient.removeQueries({
                queryKey: PurchaseQueryKeys.draftInvoice(id),
            });
            invalidateDraftInvoiceCaches(queryClient);
        },
    });
};
